/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo } from 'react';
import { useLang } from 'hooks/lang';

import { FullProject, projectStatusByCode, taskStatusByCode } from 'entities';

import { useProject } from 'providers/Project';
import { useToast } from 'providers/Toast';

import { useSyncRef } from 'hooks/syncRef';

type Callback = (...params: any) => Promise<any>;

export function useActiveOwnershipBlock<Cb extends Callback>(
  userId: string,
  cb: Cb,
): [FullProject[], Cb] {
  const { projectList, loadingProjects } = useProject();

  const { t } = useLang();
  const { addToast } = useToast();

  const activeParticipation = useMemo(() => {
    if (loadingProjects) return [];

    return projectList.filter(
      ({ owner, tasks, subOwner, status }) =>
        projectStatusByCode[status].isActiveStatus &&
        [
          [owner.id, subOwner?.id].some((id) => id === userId),

          tasks.some(
            (task) =>
              taskStatusByCode[task.status].isActiveStatus &&
              ([task.owner.id, task.subOwner?.id].some((id) => id === userId) ||
                task.subTasks.find(
                  (sub) => taskStatusByCode[sub.status].isActiveStatus && sub.owner.id === userId,
                )),
          ),
        ].some(Boolean),
    );
  }, [projectList, userId, loadingProjects]);

  const ref = useSyncRef({
    callback: cb,
  });

  const changeTeam = useCallback(
    async (...params: any[]) => {
      if (activeParticipation.length || loadingProjects)
        return addToast({
          title: t('user-has-active-ownership'),
          description: t('check-active-ownerships')!,
          type: 'error',
        });

      await ref.current.callback(...params);
    },
    [activeParticipation, ref, addToast, t, loadingProjects],
  ) as Cb;

  return [activeParticipation, changeTeam];
}
